/**
 * Shared scene geometry — the single source of truth for *where* things are.
 *
 * The PixiJS world draws objects at these virtual-pixel rectangles, and the
 * React layer ([HomeScene]) positions transparent DOM hotspot buttons over the
 * exact same rectangles (run through the canvas cover-transform). Because both
 * read from here, the clickable buttons stay pixel-aligned with the rendered
 * art at any container size / aspect ratio.
 *
 * All units are virtual pixels in a fixed 480×270 (16:9) coordinate space.
 */

export const VIRTUAL_W = 480
export const VIRTUAL_H = 270

/** Where the pavement meets the buildings. Sky above, ground below. */
export const HORIZON_Y = 160

/** Interactive zones. `life` = Reaper/Angel by the house; `pet` = cat in a tree. */
export type ZoneKey =
  | 'house'
  | 'vehicle'
  | 'travel'
  | 'life'
  | 'pet'
  | 'person'

/** The upload zones whose click → walk → (scan or gag) → upload modal. */
export type UploadZoneKey = 'house' | 'vehicle' | 'travel' | 'life' | 'pet'

export interface Rect {
  x: number
  y: number
  w: number
  h: number
}

/**
 * Bounding box of each interactive object = its visual footprint AND its
 * clickable hotspot. Laid out left→right along the street so nothing overlaps:
 * person (foreground left) · car (left-centre) · life/reaper (left of the
 * house) · house (centre-right hero) · suitcases (right). Kept within safe
 * central margins so a `cover` crop on a portrait viewport doesn't clip them.
 */
export const ZONE_RECTS: Record<ZoneKey, Rect> = {
  person: { x: 58, y: 190, w: 26, h: 52 },
  vehicle: { x: 100, y: 184, w: 102, h: 42 },
  life: { x: 206, y: 150, w: 38, h: 66 },
  house: { x: 247, y: 100, w: 118, h: 112 },
  travel: { x: 378, y: 190, w: 56, h: 42 },
  pet: { x: 434, y: 112, w: 46, h: 108 },
}

/** Order matters for paint order (back → front) in the world builder. */
export const ZONE_ORDER: readonly ZoneKey[] = [
  'house',
  'life',
  'vehicle',
  'travel',
  'pet',
  'person',
]

/** Czech labels used for the hotspot `aria-label`s. */
export const ZONE_LABEL: Record<ZoneKey, string> = {
  house: 'Dům',
  vehicle: 'Auto',
  travel: 'Kufry',
  life: 'Život',
  pet: 'Mazlíček',
  person: 'Osoba',
}

/** The character's resting X (centre of the person hotspot). */
export const CHARACTER_HOME_X = ZONE_RECTS.person.x + ZONE_RECTS.person.w / 2

/**
 * Where the character stands to scan each upload zone — just to the side of the
 * object, facing it. (Person isn't a walk target; clicking it = login.)
 */
export const SCAN_STAND_X: Record<UploadZoneKey, number> = {
  vehicle: 96,
  // The life zone collapses well left of the Reaper so the long phone-arm
  // stays clear of the centred upload modal (visible on narrow viewports too).
  life: 156,
  house: 256,
  travel: 362,
  // Stand just left of the cat tree (the character walks across the scene to it).
  pet: 418,
}
